import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Film, Tv, Star, MoreHorizontal, X, Settings, Server, Clock, Sparkles, Library as LibraryIcon, LogOut } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';

// Touch-friendly bottom nav for narrow viewports (< 768px). On desktop
// (md+) the dashboard header's icon-button row is the primary nav; this
// component is hidden there to avoid duplication. Each item is sized for
// a 44pt+ tap target per Apple HIG / Material guidelines.
//
// The desktop header has 6+ nav buttons (Library / Favorites / History /
// Onboarding / Proxy Nodes / Providers / etc.) hidden behind `md:flex`,
// so the mobile experience needed an overflow surface — otherwise mobile
// users had to request the desktop site to reach proxy-nodes, history,
// or onboarding. That surface is the "More" sheet, opened from the
// rightmost bottom-nav slot.

// Primary bottom-nav: home + the three browse surfaces that share the
// catalog (live channels via Home, VOD via Movies, series via Series).
// Favorites + Library + History move to the More sheet so the row stays
// at 4 visible tiles + a More button.
const PRIMARY_ITEMS = [
  { to: '/', label: 'Home', icon: Home, end: true },
  { to: '/movies', label: 'Movies', icon: Film, end: false },
  { to: '/series', label: 'Series', icon: Tv, end: false },
];

const MORE_ITEMS = [
  { to: '/favorites', label: 'Favorites', icon: Star, desc: 'Saved channels per family member' },
  { to: '/library', label: 'Jellyfin Library', icon: LibraryIcon, desc: 'Local-media servers' },
  { to: '/providers', label: 'Manage Providers', icon: Settings, desc: 'Add or edit IPTV providers' },
  { to: '/settings/proxy-nodes', label: 'Proxy Nodes', icon: Server, desc: 'Egress routing config' },
  { to: '/history', label: 'Watch History', icon: Clock, desc: 'Recently watched channels' },
  { to: '/onboarding', label: 'Set Up New Device', icon: Sparkles, desc: 'Pair a TV / phone' },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const handleSignOut = async () => {
    setMoreOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <>
      <nav
        role="navigation"
        aria-label="Primary"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="grid grid-cols-4">
          {PRIMARY_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[11px] font-medium ${
                    isActive ? 'text-slate-900' : 'text-gray-500 active:bg-gray-100'
                  }`
                }
              >
                <Icon className="w-5 h-5" />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-label="More options"
              aria-expanded={moreOpen}
              className="w-full flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[11px] font-medium text-gray-500 active:bg-gray-100"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>

      {moreOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="More navigation"
          className="md:hidden fixed inset-0 z-50 flex items-end"
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setMoreOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div className="relative w-full bg-white rounded-t-2xl shadow-2xl pb-[env(safe-area-inset-bottom)] animate-in slide-in-from-bottom-4">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b">
              <h2 className="text-sm font-semibold">More</h2>
              <button
                type="button"
                onClick={() => setMoreOpen(false)}
                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <ul className="py-2">
              {MORE_ITEMS.map(({ to, label, icon: Icon, desc }) => (
                <li key={to}>
                  <button
                    type="button"
                    onClick={() => {
                      setMoreOpen(false);
                      navigate(to);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] hover:bg-gray-50 active:bg-gray-100 text-left"
                  >
                    <Icon className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{desc}</p>
                    </div>
                  </button>
                </li>
              ))}
              {/* Sign out lives at the bottom of the More sheet, visually
                  separated from the nav items by a divider. Mirrors the
                  desktop header's LogOut button. */}
              <li className="border-t mt-2 pt-2">
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] hover:bg-gray-50 active:bg-gray-100 text-left"
                >
                  <LogOut className="w-5 h-5 text-gray-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">Sign out</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">End your session on this browser</p>
                  </div>
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
